import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import { v4 } from 'uuid';
import { ResourceType } from '@cotera/api';
import { CocoEvent } from '@cotera/client/app/etc';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import {
  resolveResourceNavigator,
  type ResourceNavigator,
} from '../resources/navigation';

type Params = Record<string, never>;

/**
 * Opens a new dataset.
 *
 * Nothing is created server-side: the id is minted here and the page opens on
 * it unsaved, the same way a new workflow does. The dataset is persisted by the
 * first thing the user actually does to it — adding a column, or importing a
 * CSV — so abandoning the page leaves nothing behind.
 */
export class CreateDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'New dataset';
  description = 'Open a new dataset';
  inputSchema = z.object({});
  group = 'Dataset';
  icon = 'dataset';

  private _lastCreatedDatasetId: string | null = null;

  constructor(private readonly fallbackNavigator: ResourceNavigator) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    _params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const datasetId = v4();
    this._lastCreatedDatasetId = datasetId;
    resolveResourceNavigator(context, this.fallbackNavigator).open({
      resourceType: ResourceType.Dataset,
      resourceId: datasetId,
      payload: { isNew: true },
      state: { new: true },
    });
    return ok({ resourceId: datasetId });
  }

  track(): ActionTrackMetadata | null {
    if (this._lastCreatedDatasetId === null) {
      return null;
    }
    return {
      event: CocoEvent.DatasetCreated,
      properties: { datasetId: this._lastCreatedDatasetId, type: 'manual' },
    };
  }
}
